import React from 'react'
import { Offcanvas, Spinner } from 'react-bootstrap'
import { useStripeEventTimeline } from '@data/renewals/renewals'
import { IStripeEventRow } from '@data/renewals/model/renewals.model'
import './Renewals.scss'

type TagVariant = 'success' | 'info' | 'warning' | 'danger' | 'neutral'

function variantForEvent(eventType: string): TagVariant {
  if (eventType.includes('payment_succeeded')) return 'success'
  if (eventType.includes('payment_failed')) return 'danger'
  if (eventType.includes('upcoming')) return 'warning'
  if (eventType.includes('deleted')) return 'danger'
  if (eventType.includes('created') || eventType.includes('updated')) return 'info'
  return 'neutral'
}

function formatDate(iso: string): { day: string; time: string } {
  const d = new Date(iso)
  return {
    day: d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' }).toUpperCase(),
    time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  }
}

interface Props {
  accountId: number | undefined
  show: boolean
  onHide: () => void
}

const BillingTimelineDrawer: React.FC<Props> = ({ accountId, show, onHide }) => {
  const { data: events, isLoading } = useStripeEventTimeline(accountId, show)

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: 520 }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Billing Timeline</Offcanvas.Title>
        <span className="sf-live-pulse ms-3">LIVE STRIPE STREAM</span>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="sf-card-title">STRIPE EVENTS TIMELINE (READ-ONLY)</div>
        {isLoading ? (
          <Spinner animation="border" />
        ) : (events ?? []).length === 0 ? (
          <p className="text-muted">No Stripe events recorded for this account yet.</p>
        ) : (
          <div>
            {(events ?? []).map((ev: IStripeEventRow) => {
              const variant = variantForEvent(ev.event_type)
              const { day, time } = formatDate(ev.received_at)
              return (
                <div key={ev.id} className="sf-timeline__entry">
                  <span className={`sf-timeline__dot sf-timeline__dot--${variant}`} />
                  <div style={{ minWidth: 70 }}>
                    <div className="sf-timeline__date">{day}</div>
                    <div className="text-muted small">{time}</div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="mb-1">
                      <span className={`sf-timeline__tag sf-timeline__tag--${variant}`}>
                        {ev.event_type.toUpperCase()}
                      </span>
                      <span className="fw-semibold">{ev.description ?? ''}</span>
                    </div>
                    {(ev.invoice_id || ev.subscription_id) && (
                      <div className="text-muted small">
                        {ev.invoice_id && <>Invoice: <code>{ev.invoice_id}</code>&nbsp;&nbsp;</>}
                        {ev.subscription_id && <>Subscription: <code>{ev.subscription_id}</code></>}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default BillingTimelineDrawer
